import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, LogOut, Shield, Gamepad2, UserPlus, LifeBuoy, Flame, Coins } from 'lucide-react';
import { motion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { useMe, useGeoStatus } from '../hooks/useProfile';
import { getAvailablePoints } from '../lib/points';
import { SupportChatProvider, useSupportChat } from '../hooks/useSupportChat';
import Footer from './Footer';
import SwipeOpinionLogo from './brand/SwipeOpinionLogo';
import NonUsBanner from './NonUsBanner';
import SupportChatWidget from './SupportChatWidget';
import { getAvatar } from '../lib/avatars';
import { dailyCheckin } from '../lib/api';

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/surveys', label: 'Surveys', icon: ClipboardList },
  { to: '/games', label: 'Games', icon: Gamepad2 },
  { to: '/trust-center', label: 'Trust', icon: Shield },
  { to: '/referral', label: 'Invite', icon: UserPlus },
] as const;

function getInitial(me: { display_name?: string | null; member?: { email?: string; display_name?: string | null } } | undefined): string {
  const name = me?.display_name ?? me?.member?.display_name;
  if (name) return name.charAt(0).toUpperCase();
  const email = me?.member?.email ?? '';
  return email.charAt(0).toUpperCase() || '?';
}

export default function NavShell({ children }: { children: React.ReactNode }) {
  // The support chat needs the provider to be a parent of any consumer
  // (including descendants in `children` like EarningHistoryPage's
  // Contact-support button). The actual nav chrome is a separate
  // component so it can call useSupportChat() under the provider.
  return (
    <SupportChatProvider>
      <NavShellInner>{children}</NavShellInner>
    </SupportChatProvider>
  );
}

function NavShellInner({ children }: { children: React.ReactNode }) {
  const { signOut } = useAuth();
  const { data: me } = useMe();
  const { data: geo } = useGeoStatus(!!me);
  const { pathname } = useLocation();
  const supportChat = useSupportChat();

  // Onboarding flow has its own minimal chrome — skip the support widget
  // (and its floating button) on those routes to avoid noise during the
  // sign-up funnel.
  const isOnboardingRoute = pathname.startsWith('/onboarding');

  // Show the spendable "Available" balance — the exact figure the dashboard
  // hero leads with — so the two never disagree (T4).
  const balance = getAvailablePoints(me);
  const streak = me?.streak_current ?? me?.member?.streak_current ?? 0;
  const avatar = getAvatar(me?.avatar_id ?? me?.member?.avatar_id);
  const queryClient = useQueryClient();
  const [streakToast, setStreakToast] = useState<string | null>(null);
  const checkinDone = useRef(false);

  // Auto daily check-in once per session
  useEffect(() => {
    if (!me || checkinDone.current) return;
    const lastLocal = localStorage.getItem('lastCheckinDate');
    const today = new Date().toISOString().split('T')[0];
    if (lastLocal === today) {
      checkinDone.current = true;
      return;
    }
    checkinDone.current = true;
    dailyCheckin()
      .then((result) => {
        if (!result.already_checked_in) {
          localStorage.setItem('lastCheckinDate', today);
          void queryClient.invalidateQueries({ queryKey: ['me'] });
          if (result.milestone) {
            setStreakToast(`${result.milestone.days}-Day Streak! You earned ${result.milestone.points} bonus points!`);
            setTimeout(() => setStreakToast(null), 5000);
          }
        }
      })
      .catch(() => { /* silent */ });
  }, [me, queryClient]);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col pb-16 sm:pb-0">
      <nav className="sticky top-0 z-30 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center flex-shrink-0">
            <SwipeOpinionLogo variant="full" markClassName="w-6 h-6" wordmarkClassName="text-lg text-navy" />
          </Link>

          {/* Center nav — animated pill */}
          <div className="hidden sm:flex items-center gap-1">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => {
              const active = pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  className={`relative flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-full transition-colors ${
                    active
                      ? 'text-primary font-semibold'
                      : 'text-[#6B7280] hover:text-primary'
                  }`}
                >
                  {active && (
                    <motion.div
                      layoutId="nav-pill"
                      className="absolute inset-0 bg-primary-light rounded-full"
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 flex items-center gap-1.5">
                    <Icon className="w-4 h-4" />
                    {label}
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {streak >= 1 && (
              <div className="flex items-center gap-1 bg-white border border-amber-200 text-amber-600 px-2.5 py-1.5 rounded-full text-sm font-medium tabular-nums">
                <Flame className="w-3.5 h-3.5" />
                {streak}
              </div>
            )}
            <Link to="/redeem" className="group flex items-center gap-1.5 bg-white border border-[#E5E7EB] text-[#111827] px-3 py-1.5 rounded-full text-sm font-medium tabular-nums hover:border-primary/40 transition-colors">
              <Coins className="w-4 h-4 text-amber-500 inline-block group-hover:rotate-12 transition-transform" />
              {balance.toLocaleString()}
            </Link>
            <Link to="/profile" className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              avatar ? avatar.bg : 'bg-primary text-white'
            }`}>
              {avatar ? avatar.emoji : getInitial(me)}
            </Link>
            <button
              onClick={signOut}
              className="text-[#6B7280] hover:text-[#111827] transition-colors p-1.5 rounded-lg hover:bg-gray-100"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mobile bottom nav */}
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#E5E7EB] px-2 py-1">
          {/* Mobile points row */}
          <div className="flex justify-center gap-2 py-1">
            {streak >= 1 && (
              <div className="flex items-center gap-1 border border-amber-200 text-amber-600 px-2 py-1 rounded-full text-xs font-medium tabular-nums">
                <Flame className="w-3 h-3" />{streak}
              </div>
            )}
            <div className="flex items-center gap-1.5 bg-white border border-[#E5E7EB] text-[#111827] px-3 py-1 rounded-full text-xs font-medium tabular-nums">
              <Coins className="w-3.5 h-3.5 text-amber-500" />
              {balance.toLocaleString()} pts
            </div>
          </div>
          <div className="flex justify-around">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => {
              const active = pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[11px] transition-colors ${
                    active ? 'text-primary font-semibold' : 'text-[#6B7280]'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      <div className="flex-1">
        {geo && geo.isUS === false && <NonUsBanner />}
        <div className="max-w-6xl mx-auto w-full">
          {children}
        </div>
      </div>

      <Footer />

      {/* Support chat FAB — single bottom-right help affordance. Replaced the
          legacy ChatDrawer + MessageCircle FAB on 2026-05-14 (the new chat
          covers help-questions AND account actions in one surface). Hidden
          on onboarding pages so the sign-up flow stays focused. */}
      {!isOnboardingRoute && (
        <>
          <button
            type="button"
            onClick={() => supportChat.open()}
            className="fixed bottom-24 sm:bottom-6 right-4 sm:right-6 z-40 w-14 h-14 rounded-full bg-primary text-white shadow-lg hover:bg-primary-dark hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
            aria-label="Contact support"
            title="Contact support"
          >
            <LifeBuoy className="w-6 h-6" />
          </button>
          <SupportChatWidget />
        </>
      )}

      {streakToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-amber-500 text-white text-sm font-bold px-6 py-3 rounded-2xl shadow-lg">
          {streakToast}
        </div>
      )}
    </div>
  );
}
